/**************************************************************************************************
 *
 *    Application configuration used in both client and server
 *
 *    Examples:
 *        Application title
 *        Password validation configuration
 *        Settings for translations/localization
 *
 */

import {releaseEnvAbbrev} from 'env-var-helpers';

//
// For reuse in (or as part of) multiple props
//

// Port to run the main application server on
const serverPortMain = 8080;

// Supported languages
const supportedLangs = {en: `english`, fr: `french`};

// String to add to the full admin sys href for the current environment`s subdomain (e.g. "dev.")
const envSubdomainExt = releaseEnvAbbrev === `prod` ? `` : releaseEnvAbbrev + `.`;

// Full admin sys href for the current environment
const adminDomain = `https://admin.${envSubdomainExt}canimmunize.ca/api/v2`;
const adminDomainProd = `https://admin.canimmunize.ca/api/v2`;

/******************************************** TYPINGS *********************************************/
export type SupportedLang = keyof typeof supportedLangs;

/********************************************* EXPORT *********************************************/
export const configShared = {
    login: {
        url: `/auth/login`,
    },

    /**
     * Application title / name
     */
    appTitle: `CANImmunize`,

    serverPortMain,

    validation: {
        awsConfirmationCodeLen: 6,
        minEmailLength: 3,
        maxEmailLength: 90,
        minPasswordLength: 8,
        awsMaxUsernameLength: 128,
    },

    /**
     * String formatting required in app
     */
    formatting: {
        logTimestampFull: `YYYY/MM/DD : hh:mm:ss`,
    },

    i18n: {
        defaultLang: `en`,
        // This ends up in the global "window" namespace of the client, so it's
        // important for it to have a crazy name to ensure no other variables get clobbered
        i18nNamespace: `____POLYGLOT_TRANSLATIONS_MANAGER_INSTANCE___HYDRATED_WITH_CURRENT_LOCALE_DATA____`,
        supportedLangs: Object.keys(supportedLangs) as SupportedLang[],
        supportedLangsMap: supportedLangs,
        // Tag marking location where translations JS object gets injected into root HTML file
        translationInjectionTag: `<!--<% TRANSLATION_DATA_INJECTION_POINT %>-->`,
        langInjectionTag: `<!--<% LANG_INJECTION_POINT %>-->`,
    },

    /**
     * Lists of extensions used to roughly identify file types the app can handle
     * for each type of functionality (images, code, etc)
     *     img:   treat files with these extensions as images
     *     code:  code files' extensions
     *     fonts: font files' extensions
     */
    assetExts: {
        /**
         * Built source code file extensions
         */
        code: [`js`, `css`],
        // fonts: [`woff`, `woff2`, `odt`, `ttf`]

        /**
         * Image file extensions
         */
        img: [`ico`, `png`, `svg`, `bmp`, `gif`, `tiff`],
    },

    /**
     * When a request is made to an unhandled route:
     *     path      :: If false, return a 404 page
     *                  If a URL, redirect to the given URL
     *     langAware :: If true, append /${lang} to end of URL in path property
     */
    redirectOn404: {
        path: `https://www.canimmunize.ca`,
        langAware: true,
    },

    /**
     * URLs potentially usable on both client & server
     */
    urls: {
        // prettier-ignore
        adminSystem: {
            banners:           {method: `GET`, url: `${adminDomain}/banners`            },
            bannersProd:       {method: `GET`, url: `${adminDomainProd}/banners`        },
            cities:            {method: `GET`, url: `${adminDomain}/cities`             },
            configMain:        {method: `GET`, url: `${adminDomain}/config/main`        },
            configOph:         {method: `GET`, url: `${adminDomain}/config/oph`         },
            diseases:          {method: `GET`, url: `${adminDomain}/diseases`           },
            diseaseConcepts:   {method: `GET`, url: `${adminDomain}/disease-concepts`   },
            outbreaks:         {method: `GET`, url: `${adminDomain}/outbreaks`          },
            schedules:         {method: `GET`, url: `${adminDomain}/schedules`          },
            tradenameConcepts: {method: `GET`, url: `${adminDomain}/trade-name-concepts`},
            vaccines:          {method: `GET`, url: `${adminDomain}/vaccines`           },
            vaccineConcepts:   {method: `GET`, url: `${adminDomain}/vaccine-concepts`   },
        }
    },

    /**
     * Set to true to turn websockets on (by activating socket.io)
     */
    isSocketIoOn: false,
};
